// frontend/packages/business-console/src/pages/ReportEmailsSettings.jsx
//
// Settings > Report emails. Choose who gets the scheduled lead report
// (weekly or monthly leads with a spreadsheet attached), e.g. a marketing
// partner. Each new address gets a confirmation email first and nothing else
// until it is confirmed; every report has an unsubscribe link. Business
// admins manage the list; other roles see it read-only.
//
// Per-tenant feature (`report_emails`); Settings only shows this tab when it
// is on.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import { useAuth } from '../contexts/AuthContext';
import {
  useTokens,
  PageHeader,
  Surface,
  StatusPill,
} from '../components/primitives';
import {
  listReportRecipients,
  addReportRecipient,
  updateReportRecipient,
  removeReportRecipient,
  resendReportConfirmation,
} from '../services/reportRecipientsService';

// Same rule as the server (services/reportRecipientsService.js): a bare
// address only, no display names or brackets.
const EMAIL_RE =
  /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/;

const STATUS = {
  pending: { kind: 'warning', label: 'Waiting for confirmation' },
  active: { kind: 'success', label: 'Receiving reports' },
  unsubscribed: { kind: 'neutral', label: 'Unsubscribed' },
};

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function messageOf(err, fallback) {
  return err?.response?.data?.message || fallback;
}

export default function ReportEmailsSettings() {
  const t = useTokens();
  const { user, businessId, isStaff } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [email, setEmail] = useState('');
  const [frequency, setFrequency] = useState('weekly');

  // Mirror the server gate so non-admins don't see controls that 403.
  const canManage = useMemo(() => {
    if (isStaff) return true;
    const access = Array.isArray(user?.businessAccess)
      ? user.businessAccess
      : [];
    return access.some(
      (e) =>
        String(e?.businessId) === String(businessId) &&
        String(e?.role || '').toLowerCase() === 'businessadmin'
    );
  }, [user, businessId, isStaff]);

  const load = useCallback(async () => {
    try {
      setData(await listReportRecipients());
    } catch (err) {
      setError(messageOf(err, 'Could not load report emails.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn, okMessage) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (okMessage) setNotice(okMessage);
      return true;
    } catch (err) {
      setError(messageOf(err, 'Something went wrong. Please try again.'));
      return false;
    } finally {
      // Reload either way: a failed confirmation send still adds the address
      // (as pending, ready for Resend).
      await load();
      setBusy(false);
    }
  };

  const trimmed = email.trim();
  const emailValid = EMAIL_RE.test(trimmed);
  const recipients = data?.recipients || [];
  const atCap = recipients.length >= (data?.maxRecipients || 10);

  const handleAdd = async () => {
    if (!emailValid) return;
    const ok = await run(
      () => addReportRecipient({ email: trimmed, frequency }),
      `We emailed ${trimmed} a confirmation link. Reports start once they confirm.`
    );
    if (ok) setEmail('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 880, mx: 'auto', width: '100%' }}>
      <PageHeader title="Report emails" sx={{ mb: 0.75 }} />
      <Typography sx={{ fontSize: 13.5, color: t.muted, mb: 3 }}>
        Send your lead report to the people who need it, like your marketing
        partner. Each report shows how many website leads you got and how they
        heard about you, with a spreadsheet attached. Weekly reports go out
        Monday morning; monthly reports on the 1st.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setNotice(null)}
        >
          {notice}
        </Alert>
      ) : null}

      {data && !data.enabled ? (
        <Surface sx={{ p: 2.5 }}>
          <Typography sx={{ fontSize: 13.5, color: t.muted }}>
            Report emails aren&rsquo;t turned on for your account. Contact
            UpRyzze to turn them on.
          </Typography>
        </Surface>
      ) : (
        <>
          {canManage ? (
            <Surface sx={{ p: 2.5, mb: 2 }}>
              <Typography
                sx={{ fontSize: 13, fontWeight: 800, color: t.text, mb: 1.5 }}
              >
                Add a recipient
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.25,
                  alignItems: 'flex-start',
                }}
              >
                <TextField
                  size="small"
                  type="email"
                  label="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAdd();
                  }}
                  error={trimmed !== '' && !emailValid}
                  helperText={
                    trimmed !== '' && !emailValid
                      ? 'Enter a valid email address.'
                      : ' '
                  }
                  disabled={busy || atCap}
                  sx={{ flex: '1 1 240px', minWidth: 0 }}
                />
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <Select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    disabled={busy || atCap}
                    inputProps={{ 'aria-label': 'How often' }}
                  >
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={handleAdd}
                  disabled={busy || atCap || !emailValid}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '9px',
                    height: 40,
                  }}
                >
                  Add
                </Button>
              </Box>
              <Typography sx={{ fontSize: 12, color: t.faint }}>
                {atCap
                  ? `You've reached the limit of ${data?.maxRecipients || 10} recipients. Remove one to add another.`
                  : 'We email them a confirmation link first. Nothing else is sent until they confirm, and every report has an unsubscribe link.'}
              </Typography>
            </Surface>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Only business admins can change who receives reports.
            </Alert>
          )}

          <Surface sx={{ p: 0 }}>
            {recipients.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: t.muted, p: 2.5 }}>
                No one receives reports yet.
              </Typography>
            ) : (
              recipients.map((r, i) => {
                const st = STATUS[r.status] || STATUS.pending;
                const sub =
                  r.status === 'active' && r.lastSentAt
                    ? `Last sent ${fmtDate(r.lastSentAt)}`
                    : r.status === 'pending' && r.confirmSentAt
                      ? `Confirmation sent ${fmtDate(r.confirmSentAt)}`
                      : '';
                return (
                  <Box
                    key={r.id}
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 1.25,
                      px: 2.5,
                      py: 1.5,
                      borderTop: i ? `1px solid ${t.hairline}` : 'none',
                    }}
                  >
                    <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                      <Typography
                        sx={{ fontSize: 13.5, fontWeight: 600, color: t.text }}
                        noWrap
                      >
                        {r.email}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          mt: 0.5,
                          flexWrap: 'wrap',
                        }}
                      >
                        <StatusPill size="sm" kind={st.kind} label={st.label} />
                        {sub ? (
                          <Typography sx={{ fontSize: 11.5, color: t.faint }}>
                            {sub}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                    {canManage && r.status !== 'unsubscribed' ? (
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={r.frequency}
                          disabled={busy}
                          inputProps={{
                            'aria-label': `How often for ${r.email}`,
                          }}
                          onChange={(e) =>
                            run(
                              () =>
                                updateReportRecipient(r.id, {
                                  frequency: e.target.value,
                                }),
                              `${r.email} now gets ${e.target.value} reports.`
                            )
                          }
                        >
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          color: t.muted,
                          textTransform: 'capitalize',
                        }}
                      >
                        {r.frequency}
                      </Typography>
                    )}
                    {canManage && r.status === 'pending' ? (
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() =>
                          run(
                            () => resendReportConfirmation(r.id),
                            `Confirmation re-sent to ${r.email}.`
                          )
                        }
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        Resend
                      </Button>
                    ) : null}
                    {canManage ? (
                      <Tooltip title="Remove">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Remove ${r.email}`}
                            disabled={busy}
                            onClick={() =>
                              run(
                                () => removeReportRecipient(r.id),
                                `${r.email} removed.`
                              )
                            }
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : null}
                  </Box>
                );
              })
            )}
          </Surface>
        </>
      )}
    </Box>
  );
}
